import React, { useState, useEffect } from 'react';
import pluralize from 'pluralize';
import { DropdownMenu, TableList, TableListActions, Typography, useOverlayContext } from 'seagull';
import { formatDateLong, formatDateWithTime } from "../../../models/time/selectors";
import { templateTypeToDropdownLabel, useEditIntroMessage } from "../../../components/AgreementStudio/IntroMessage/IntroMessage.utils";
import { IntroVideoType } from "../../../models/intro-video";
import compact from 'lodash/compact';
import { EditDraftsTableColumns } from "./types";
import styles from "./EditDraftsTableRow.module.scss";
import { CopyFill, TrashLiner24 } from 'seagull/icons';
import { getUICompanyName } from "./EditDraftsTable.utils";
import ContactCell from "./cells/ContactCell";
import AdditionalRecipientsCell from "./cells/AdditionalRecipientsCell";
import AgreementNameCell from "./cells/AgreementNameCell";
import DraftUpdateModal from "./DraftUpdateModal";
function makeEditableTableItem(value) {
  return {
    className: styles.cell,
    value
  };
}
const testIdPrefix = 'edit-drafts-table';
const EditDraftsTableRow = props => {
  var _draft$lastModifiedBy, _draft$lastModifiedBy2, _draft$expirationDate, _draft$introVideo, _draft$legalTerms, _draft$legalTerms2, _draft$amendmentsAppr, _draft$invoicesApprov;
  const {
    index,
    visibleColumns,
    getContact
  } = props;
  const [draft, setDraft] = useState(props.draft);
  useEffect(() => setDraft(props.draft), [props.draft]);
  const contact = getContact(draft);
  const draftLabel = getUICompanyName(draft, contact);
  const {
    getTemplateTypeByMessage
  } = useEditIntroMessage();
  const {
    showModal
  } = useOverlayContext();
  const openDraftUpdateModal = _ref => {
    let {
      width,
      renderContent,
      shouldSubmit
    } = _ref;
    showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref2 => {
        let {
          dismissModal
        } = _ref2;
        return /*#__PURE__*/React.createElement(DraftUpdateModal, {
          draft: draft,
          width: width,
          renderContent: renderContent,
          dismissModal: dismissModal,
          changeDraftInRow: setDraft,
          shouldSubmit: shouldSubmit
        });
      }
    });
  };
  return /*#__PURE__*/React.createElement(TableList.Row, {
    rowId: draft.id,
    "data-testid": "".concat(testIdPrefix, "-row-").concat(draft.id),
    items: compact([makeEditableTableItem( /*#__PURE__*/React.createElement(ContactCell, {
      draft: draft,
      contact: contact,
      index: index,
      onOpenModal: openDraftUpdateModal
    })), visibleColumns[EditDraftsTableColumns.AdditionalRecipients] && makeEditableTableItem( /*#__PURE__*/React.createElement(AdditionalRecipientsCell, {
      draft: draft,
      contact: contact,
      index: index,
      onOpenModal: openDraftUpdateModal
    })), visibleColumns[EditDraftsTableColumns.AgreementName] && makeEditableTableItem( /*#__PURE__*/React.createElement(AgreementNameCell, {
      draft: draft,
      index: index,
      onOpenModal: openDraftUpdateModal
    })), visibleColumns[EditDraftsTableColumns.EffectiveDate] && {
      value: /*#__PURE__*/React.createElement(Typography, {
        "data-testid": "effective-date-".concat(index)
      }, draft.effectiveDate ? formatDateLong(draft.effectiveDate) : 'On acceptance')
    }, visibleColumns[EditDraftsTableColumns.ModifiedBy] && {
      value: /*#__PURE__*/React.createElement(Typography, {
        "data-testid": "last-modified-by-".concat(index)
      }, (_draft$lastModifiedBy = draft.lastModifiedBy) === null || _draft$lastModifiedBy === void 0 ? void 0 : _draft$lastModifiedBy.firstName, " ", (_draft$lastModifiedBy2 = draft.lastModifiedBy) === null || _draft$lastModifiedBy2 === void 0 ? void 0 : _draft$lastModifiedBy2.lastName)
    }, visibleColumns[EditDraftsTableColumns.LastModified] && {
      value: /*#__PURE__*/React.createElement(Typography, {
        "data-testid": "last-modified-on-".concat(index)
      }, formatDateWithTime(draft.lastModifiedOn))
    }, visibleColumns[EditDraftsTableColumns.ExpirationDate] && {
      value: /*#__PURE__*/React.createElement(Typography, {
        "data-testid": "expiration-date-".concat(index)
      }, ((_draft$expirationDate = draft.expirationDate) === null || _draft$expirationDate === void 0 ? void 0 : _draft$expirationDate.type) === 'days' ? "".concat(draft.expirationDate.days, " ").concat(pluralize('day', draft.expirationDate.days), " after sending") : '')
    }, visibleColumns[EditDraftsTableColumns.IntroductoryMessage] && {
      value: /*#__PURE__*/React.createElement(Typography, {
        ellipsis: true,
        "data-testid": "introductory-message-".concat(index)
      }, templateTypeToDropdownLabel[getTemplateTypeByMessage(draft.customNotificationText)])
    }, visibleColumns[EditDraftsTableColumns.IntroductoryVideo] && {
      value: /*#__PURE__*/React.createElement(Typography, {
        ellipsis: true,
        "data-testid": "introductory-video-".concat(index)
      }, ((_draft$introVideo = draft.introVideo) === null || _draft$introVideo === void 0 ? void 0 : _draft$introVideo.type) === IntroVideoType.VideoLink ? draft.introVideo.videoLink.url : '')
    }, visibleColumns[EditDraftsTableColumns.TermsOfService] && {
      value: /*#__PURE__*/React.createElement(Typography, {
        "data-testid": "terms-of-service-".concat(index)
      }, ((_draft$legalTerms = draft.legalTerms) === null || _draft$legalTerms === void 0 ? void 0 : _draft$legalTerms.type) === 'libraryLegalTermsId' ? '' : (_draft$legalTerms2 = draft.legalTerms) === null || _draft$legalTerms2 === void 0 ? void 0 : _draft$legalTerms2.displayName)
    }, visibleColumns[EditDraftsTableColumns.AgreementNote] && {
      value: /*#__PURE__*/React.createElement(Typography, {
        ellipsis: true,
        "data-testid": "agreement-note-".concat(index)
      }, draft.extraClause)
    }, visibleColumns[EditDraftsTableColumns.AutoApproveAmendments] && {
      value: /*#__PURE__*/React.createElement(Typography, {
        "data-testid": "auto-approve-amendments-".concat(index)
      }, ((_draft$amendmentsAppr = draft.amendmentsApprovalMethod) === null || _draft$amendmentsAppr === void 0 ? void 0 : _draft$amendmentsAppr.type) === 'auto' ? "".concat(draft.amendmentsApprovalMethod.auto.noticePeriodDays, " ").concat(pluralize('day', draft.amendmentsApprovalMethod.auto.noticePeriodDays), " notice") : 'Disabled')
    }, visibleColumns[EditDraftsTableColumns.AutoApproveOutOfScopeCharges] && {
      value: /*#__PURE__*/React.createElement(Typography, {
        "data-testid": "auto-approve-out-of-scope-charges-".concat(index)
      }, ((_draft$invoicesApprov = draft.invoicesApprovalMethod) === null || _draft$invoicesApprov === void 0 ? void 0 : _draft$invoicesApprov.type) === 'auto' ? "".concat(draft.invoicesApprovalMethod.auto.noticePeriodDays, " ").concat(pluralize('day', draft.invoicesApprovalMethod.auto.noticePeriodDays), " notice") : 'Disabled')
    }, visibleColumns[EditDraftsTableColumns.NumberOfDaysUntilPaymentIsDue] && {
      value: /*#__PURE__*/React.createElement(Typography, {
        "data-testid": "number-of-days-until-payment-is-due-".concat(index)
      }, draft.netTerms ? "".concat(draft.netTerms, " ").concat(pluralize('day', draft.netTerms)) : 'Upon receipt of invoice')
    }, visibleColumns[EditDraftsTableColumns.PaymentMethodRequiredForSigning] && {
      value: /*#__PURE__*/React.createElement(Typography, {
        "data-testid": "payment-method-required-for-signing-".concat(index)
      }, draft.requirePaymentMethod ? 'No' : 'Yes')
    }, visibleColumns[EditDraftsTableColumns.CreditCardProcessingFees] && {
      value: /*#__PURE__*/React.createElement(Typography, {
        "data-testid": "credit-card-processing-fees-".concat(index)
      }, draft.feeControls.coverCreditCardFees ? "Paid by ".concat(draftLabel) : 'Paid by the client')
    }, visibleColumns[EditDraftsTableColumns.ManualApprovalOfEachPayment] && {
      value: /*#__PURE__*/React.createElement(Typography, {
        "data-testid": "manual-approval-of-each-payment-".concat(index)
      }, draft.allowManualPayments ? 'Yes' : 'No')
    }]),
    actionColumn: /*#__PURE__*/React.createElement(TableListActions, null, /*#__PURE__*/React.createElement(DropdownMenu.Item, {
      iconStart: /*#__PURE__*/React.createElement(CopyFill, null)
    }, "Duplicate Draft"), /*#__PURE__*/React.createElement(DropdownMenu.Item, {
      skin: "error",
      iconStart: /*#__PURE__*/React.createElement(TrashLiner24, null)
    }, "Discard Draft"))
  });
};
export default EditDraftsTableRow;